/* eslint-disable @typescript-eslint/no-explicit-any */
// ** Roles Slice - store/slices/rolesSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { roleService } from "@src/@api-services/role";
import type { TableSortValue } from "@src/shared/constants/sort-order.constants";

// ** Types
interface AddRole {
  name: string;
  shortDescription: string;
  permissions?: number[];
  status?: string;
}

interface Role {
  id: string | number;
  name: string;
  permissions?: string[];
  status?: string;
}

interface GetRolesParams {
  page?: number;
  limit?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: TableSortValue;
  /** When true, updates permission options without triggering the list table loader. */
  silent?: boolean;
  [key: string]: any;
}

interface RolesState {
  data: Role[];
  total: number;
  params: GetRolesParams;
  allData: Role[];
  selectedRole: Role | null;
  rolesLoading: boolean;
  permissionsLoading: boolean;
  error: string | null;
  dropDownRoles: Role[];
  permissions: any[];
  allPermissionData: any[];
  totalPermissions: number;
}

// ** Async Thunks
export const getAllRoles = createAsyncThunk(
  "roles/getAllRoles",
  async (query: GetRolesParams = {}, { rejectWithValue }) => {
    try {
      const response = await roleService.getRoles(query);
      return response?.data?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all roles"
      );
    }
  }
);

export const getAllPermissions = createAsyncThunk(
  "roles/getAllPermissions",
  async (params: GetRolesParams = {}, { rejectWithValue }) => {
    try {
      const response = await roleService.getPermissions(params);

      let allPermissions = [];
      if (Array.isArray(response?.data?.data)) {
        allPermissions = response.data.data;
      }
      return {
        params,
        data: response?.data?.data?.data || [],
        allPermissions: allPermissions || [],
        total: response?.data?.data?.totalRecord || 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all permissions"
      );
    }
  }
);

export const getRoles = createAsyncThunk(
  "roles/getRoles",
  async (params: GetRolesParams, { rejectWithValue }) => {
    try {
      const response = await roleService.getRoles(params);
      return {
        params,
        data: response.data.data.data || [],
        total: response.data.data.totalRecord || 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch roles"
      );
    }
  }
);

export const getRole = createAsyncThunk(
  "roles/getRole",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await roleService.getRole(id);
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch role"
      );
    }
  }
);

export const addRole = createAsyncThunk(
  "roles/createRole",
  async (roleData: Partial<AddRole>, { rejectWithValue }: any) => {
    try {
      const response = await roleService.createRole(roleData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create role"
      );
    }
  }
);

export const addPermission = createAsyncThunk(
  "permission/createPermission",
  async (permissionData: Partial<any>, { rejectWithValue }: any) => {
    try {
      const response = await roleService.createPermission(permissionData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create permission"
      );
    }
  }
);

export const updateRole = createAsyncThunk(
  "roles/updateRole",
  async (
    { id, roleData }: { id: string | number; roleData: Partial<AddRole> },
    { rejectWithValue }: any
  ) => {
    try {
      const response = await roleService.updateRole(id, roleData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update role"
      );
    }
  }
);

export const updatePermission = createAsyncThunk(
  "permission/updatePermission",
  async (
    {
      id,
      permissionData,
    }: { id: string | number; permissionData: Partial<any> },
    { rejectWithValue }: any
  ) => {
    try {
      const response = await roleService.updatePermission(id, permissionData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update permission"
      );
    }
  }
);

export const deleteRole = createAsyncThunk(
  "roles/deleteRole",
  async (id: string | number, { rejectWithValue }: any) => {
    try {
      await roleService.deleteRole(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete role"
      );
    }
  }
);

export const deletePermission = createAsyncThunk(
  "permission/deletePermission",
  async (id: string | number, { rejectWithValue }: any) => {
    try {
      await roleService.deletePermission(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete role"
      );
    }
  }
);

export const getRolesDropdown = createAsyncThunk(
  "roles/getRolesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await roleService.getRolesDropdown();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch dropdown roles"
      );
    }
  }
);

// ** Slice
const initialState: RolesState = {
  data: [],
  total: 0,
  params: {},
  allData: [],
  selectedRole: null,
  rolesLoading: false,
  permissionsLoading: false,
  error: null,
  dropDownRoles: [],
  permissions: [],
  allPermissionData: [],
  totalPermissions: 0,
};

export const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedRole: (state) => {
      state.selectedRole = null;
    },
    setParams: (state, action) => {
      state.params = action.payload;
    },
    updateRoleInList: (state, action) => {
      const { id, updatedData } = action.payload;
      const index = state.data.findIndex((role) => role.id === id);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...updatedData };
      }
    },
    removeRoleFromList: (state, action) => {
      state.data = state.data.filter((role) => role.id !== action.payload);
      state.total = Math.max(0, state.total - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllRoles.pending, (state) => {
        state.rolesLoading = true;
        state.error = null;
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.params = action.meta.arg || {};
        const payload = action.payload || {};
        state.allData = Array.isArray(payload)
          ? payload
          : payload.data || [];
        state.total =
          payload.totalRecord ?? payload.total ?? state.allData.length;
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.error = action.payload as string;
        state.allData = [];
        state.total = 0;
      })

      .addCase(getRoles.pending, (state) => {
        state.rolesLoading = true;
        state.error = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })

      .addCase(getRole.pending, (state) => {
        state.error = null;
      })
      .addCase(getRole.fulfilled, (state, action) => {
        state.selectedRole = action.payload;
      })
      .addCase(getRole.rejected, (state) => {
        state.selectedRole = null;
      })

      .addCase(getRolesDropdown.pending, (state) => {
        state.error = null;
      })
      .addCase(getRolesDropdown.fulfilled, (state, action) => {
        state.dropDownRoles = action.payload;
      })
      .addCase(getRolesDropdown.rejected, (state, action) => {
        state.error = action.payload as string;
        state.dropDownRoles = [];
      })
      .addCase(getAllPermissions.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.permissionsLoading = true;
        }
        state.error = null;
      })
      .addCase(getAllPermissions.fulfilled, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.permissionsLoading = false;
        }
        state.params = action.meta.arg || {};
        const payload = action.payload || {};
        state.allPermissionData = payload.data || [];
        state.permissions = payload.allPermissions || [];
        state.totalPermissions = payload.total || 0;
      })
      .addCase(getAllPermissions.rejected, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.permissionsLoading = false;
        }
        state.error = action.payload as string;
        state.allPermissionData = [];
        state.permissions = [];
        state.totalPermissions = 0;
      });
  },
});

export const {
  clearError,
  clearSelectedRole,
  setParams,
  updateRoleInList,
  removeRoleFromList,
} = rolesSlice.actions;

export default rolesSlice.reducer;
